import { NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CellTransform, TableConfig, TableFilter } from '@app/interfaces';
import { UnitListItemForEdit } from '@app/models';
import { UnitListItemService } from '@app/shared/services';
import { NumbersOnlyDirective } from '../../directives/numbers-only.directive';
import { ExistingDataDuplicateValueValidatorDirective } from '../../directives/validators/existing-data-duplicate-value-validator.directive';
import { MaxDecimalPlacesValidatorDirective } from '../../directives/validators/max-decimal-places-validator.directive';
import { MaxValidatorDirective } from '../../directives/validators/max-validator.directive';
import { RequiredNoWhitespaceDirective } from '../../directives/validators/required-no-whitespace.directive';
import { FiltersComponent } from '../../forms/components/filters/filters.component';
import { ModalComponent } from '../modal/modal.component';
import { TableComponent } from '../table/table.component';

export interface UnitListItemsSetupConfig {
	entityName: string;
	fieldName: string;
	isReadOnly?: boolean;
	hideActive?: boolean;
	hideDescription?: boolean;
	hidePriority?: boolean;
	keyHeader?: string;
	valueHeader?: string;
	valueTemplate?: 'textarea' | 'months';
}

@Component({
    selector: 'sl-unit-list-items-setup',
    templateUrl: './unit-list-items-setup.component.html',
    standalone: true,
    imports: [
        MatCardModule,
        FiltersComponent,
        NgIf,
        MatButtonModule,
        MatIconModule,
        TableComponent,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        RequiredNoWhitespaceDirective,
        ReactiveFormsModule,
        ExistingDataDuplicateValueValidatorDirective,
        NumbersOnlyDirective,
        MaxDecimalPlacesValidatorDirective,
        MaxValidatorDirective,
    ],
})
export class UnitListItemsSetupComponent implements OnInit, OnChanges {
	@ViewChild('keyTemplate') keyTemplate: TemplateRef<any>;
	@ViewChild('displayTemplate') displayTemplate: TemplateRef<any>;
	@ViewChild('displayMonthsTemplate') displayMonthsTemplate: TemplateRef<any>;
	@ViewChild('displayTextAreaTemplate') displayTextAreaTemplate: TemplateRef<any>;

	@Input() config: UnitListItemsSetupConfig;
	@Output() unsavedChanges: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() itemsLoaded: EventEmitter<UnitListItemForEdit[]> = new EventEmitter<UnitListItemForEdit[]>();

	entities: UnitListItemForEdit[] = [];
	newRow: any = null;
	newRowId = -1;

	tableConfig: TableConfig<UnitListItemForEdit> = {
		columns: [
			{
				header: (): string => this.config.keyHeader ?? 'Code',
				key: 'KeyValue',
				template: (): TemplateRef<any> => this.keyTemplate,
			},
			{
				header: (): string => this.config.valueHeader ?? 'Description',
				key: 'DisplayValue',
				template: (): TemplateRef<any> =>
					this.config.valueTemplate === 'months'
						? this.displayMonthsTemplate
						: this.config.valueTemplate === 'textarea'
						? this.displayTextAreaTemplate
						: this.displayTemplate,
				hidden: (): boolean => this.config.hideDescription,
			},
			{
				header: 'Active',
				key: 'Active',
				transform: ({ row }: CellTransform<UnitListItemForEdit>): string => {
					const icon = row.Active ? 'done' : 'close';
					const cssClass = row.Active ? 'success' : '';
					return `<mat-icon class="${cssClass}">${icon}</mat-icon>`;
				},
				template: 'checkbox',
				hidden: (): boolean => this.config.hideActive,
			},
		],
		editActions: {
			delete: (row: UnitListItemForEdit): void => {
				this.deleteRow(row);
			},
			update: (row: UnitListItemForEdit): void => {
				this.updateRow(row);
			},
			allowDelete: (row: UnitListItemForEdit): boolean => !row.IsReferenced,
			allowUpdate: (row: UnitListItemForEdit): boolean => !row.IsReferenced && !row.IsLocked,
		},
		enableMove: true,
		onDrop: (row: UnitListItemForEdit, offset) => {
			if (!this.tableConfig.isReadOnly) {
				this.updatePosition(row, offset);
			}
		},
		defaultSortDirection: 'asc',
		defaultSortField: 'Priority',
		defaultPageSize: 100,
		sortingDisabled: true,
		showRowActions: false,
		showRowSelect: false,
		editableRows: true,
	};

	filters = { search: '' };

	constructor(private readonly dialog: MatDialog, private readonly service: UnitListItemService) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.config?.currentValue !== null) {
			this.tableConfig.isReadOnly = this.config.isReadOnly;
			this.tableConfig.defaultSortField = this.config.hidePriority ? 'KeyValue' : 'Priority';
			this.tableConfig.defaultPageSize = this.config.hidePriority ? 10 : 100;
			this.tableConfig.enableMove = !this.config.hidePriority;
		}
	}

	ngOnInit(): void {
		this.loadData();
	}

	add(): void {
		const maxPriority = this.entities.length > 0 ? Math.max(...this.entities.map((s) => s.Priority)) : 0;

		const newEntity: Partial<UnitListItemForEdit> = {
			Id: this.newRowId,
			Active: true,
			FieldName: this.config.fieldName,
			Priority: maxPriority + 1,
			IsReferenced: false,
		};

		this.entities = [...this.entities, newEntity as UnitListItemForEdit];
		this.newRow = newEntity;
		this.newRowId--;
	}

	deleteRow(row: UnitListItemForEdit): void {
		if (row.Id > 0) {
			this.dialog
				.open(ModalComponent, {
					data: {
						title: 'Confirm Delete',
						message: `Delete ${this.config.entityName}? This will be deleted permanently.`,
					},
				})
				.afterClosed()
				.subscribe((dialogResult) => {
					if (!dialogResult) {
						return;
					}
					this.service.deleteEntity(row.Id).subscribe(() => {
						this.entities = [...this.entities.filter((d) => d.Id !== row.Id)];
						this.itemsLoaded.emit(this.entities);
					});
				});
		} else {
			this.entities = [...this.entities.filter((d) => d.Id !== row.Id)];
			this.itemsLoaded.emit(this.entities);
		}
	}

	updateRow(row: UnitListItemForEdit): void {
		if (this.config.hideDescription) {
			row.DisplayValue = row.KeyValue;
		}
		if (row.Id > 0) {
			this.service.updateEntity(row).subscribe(() => {
				this.itemsLoaded.emit(this.entities);
			});
		} else {
			this.service.createEntity(row).subscribe((data: any) => {
				Object.assign(row, data);
				this.itemsLoaded.emit(this.entities);
			});
		}
	}

	updatePosition(row: UnitListItemForEdit, positionDifference: number): void {
		this.service.updatePosition(row.Id, positionDifference).subscribe(() => {
			this.loadData();
		});
	}

	getOtherKeyValues(id: number): () => string[] {
		return () => this.entities.filter((item) => item.Id !== id).map((item) => item.KeyValue);
	}

	getOtherDisplayValues(id: number): () => string[] {
		return () => this.entities.filter((item) => item.Id !== id).map((item) => item.DisplayValue);
	}

	applyFilter(filterValue: Partial<TableFilter<UnitListItemForEdit>>): void {
		filterValue.search = filterValue.search.trim().toLowerCase();

		this.filters = { ...this.filters, ...filterValue };
	}

	private loadData(): void {
		this.service.getList(this.config.fieldName).subscribe((data: UnitListItemForEdit[]) => {
			this.entities = data;
			this.itemsLoaded.emit(this.entities);
		});
	}
}
